import api from '../../utils/api';
import {
  UseResource,
  createCachedResource,
  useCachedResource,
  userFacingError,
} from '../../utils/cachedResource';

/**
 * 질환·장기 디지털트윈 조회 — AP_009_002
 *
 * 출처: Notion "APP - DT"
 *   https://skillful-tuba-487.notion.site/APP-DT-3b1c8bb3498280439dd8e1e74f4729c1
 *
 * 주의: 헤더 `X-CUSTOMER-ID` 에 **familyId** 를 넣는다.
 *   문진 저장(AP_009_001)은 customerId 라서 서로 다르다. 검진결과 테이블이
 *   familyId 로 인덱스되어 있다는 것이 스펙에 명시된 이유다.
 *   → [[diseaseSurvey]] 의 submitDiseaseSurvey 와 헤더 의미가 다른 점 주의.
 */

// ── 응답 타입 ────────────────────────────────────────────────

/** 장기 판단 결과. riskGrade 는 'Grade 0' | 'Grade 1' 형태. */
export type OrganRisk = {
  organName: string;
  riskGrade: string;
  confidenceLevel: string;
  dataCoverageRate: number;
  score: number;
  /**
   * 서버가 생성하는 장기 상태 요약 (2026-08-13 스펙 추가, 실측 100~110자).
   * 화면이 들고 있던 고정 문구를 대체한다.
   *
   * optional 인 이유: 이 필드는 dev 에만 반영돼 있다. 운영(apiv2)에는 AP_009_002
   * 자체가 아직 배포되지 않았으므로(docs/백엔드-이슈-디지털트윈.md 1번),
   * 배포 시점에 따라 없을 수 있다. 없으면 해당 구간을 그리지 않는다.
   */
  organSummary?: string;
  /** 서버가 생성하는 장기별 생활 관리 조언 (실측 120~140자) */
  detailedAnalysis?: string;
};

/** 질환 판단 결과. riskGrade 는 '미세 위험 - 지질 관리 필요' 같은 서버 생성 문구. */
export type DiseaseRiskDetail = {
  diseaseName: string;
  riskGrade: string;
  confidenceLevel: string;
  dataCoverageRate: number;
  score: number;
  /** 판단에 사용된 지표 */
  evidenceData: string[];
  /** 결측 지표 — 스펙이 의도적으로 숨기지 않고 내려준다 */
  missingData: string[];
  interpretation: string;
  /**
   * 서버가 생성하는 질환 위험 요약 (2026-08-13 스펙 추가, 실측 110~130자).
   * 화면이 들고 있던 고정 설명문(diseaseInfo.ts)을 대체한다.
   * optional 인 이유는 OrganRisk.organSummary 주석 참고.
   */
  diseaseSummary?: string;
  /** 서버가 생성하는 질환별 관리 조언 (실측 145~150자) */
  detailedAnalysis?: string;
};

/** 스펙에 명시된 검진 지표 18종. 값이 없으면 null 로 내려온다. */
export type HealthIndicators = {
  HBA1C: number | null;
  FASTING_GLUCOSE: number | null;
  BMI: number | null;
  WAIST_CIRCUMFERENCE: number | null;
  TRIGLYCERIDE: number | null;
  HDL_CHOLESTEROL: number | null;
  LDL_CHOLESTEROL: number | null;
  TOTAL_CHOLESTEROL: number | null;
  GAMMA_GTP: number | null;
  AST: number | null;
  ALT: number | null;
  SYSTOLIC_BP: number | null;
  DIASTOLIC_BP: number | null;
  eGFR: number | null;
  CREATININE: number | null;
  URINE_PROTEIN: number | null;
  HEIGHT: number | null;
  WEIGHT: number | null;
};

export type DigitalTwinUser = {
  customerId: string;
  userName: string;
  age: number;
  /**
   * true = 남성 / false = 여성.
   *
   * 스펙에 어느 쪽이 남성인지 적혀 있지 않아 실측으로 확정했다. 여성 계정에서
   * 세 곳이 모두 일치한다 — AP_009_002 `user.sex`=false, `permission/getProfile`
   * `sex`='F' · `gender`='0'. 가입 화면(Join/UserJoinPrivate)도 남성 토글을
   * `sex: alignment !== '0'` 로 true 로 보낸다.
   */
  sex: boolean;
  netCalories: number;
  burnedCalories: number;
  /**
   * 저장된 건강 문진. 미제출이면 null (스펙 예시가 null 이다).
   * 채워진 형태는 스펙에 예시가 없어 unknown 으로 두고 hasSubmittedSurvey 로만 판단한다.
   */
  survey: unknown | null;
  healthIndicators: HealthIndicators;
};

/** 질환 4종 키 — AP_009_001 요청 키와 동일하다 */
export type DiseaseApiKey = 'diabetes' | 'hypertension' | 'dyslipidemia' | 'obesity';

export type DigitalTwinData = {
  /** 한글 장기명이 키다 ('췌장' | '폐' | '심장' | '대장' | '간' | '뇌' | '신장') */
  organ: Record<string, OrganRisk>;
  disease: Record<DiseaseApiKey, DiseaseRiskDetail>;
  user: DigitalTwinUser;
};

// ── 장기 매핑 ────────────────────────────────────────────────

/**
 * 화면의 장기 키 → 서버 응답의 한글 장기명.
 *
 * 서버는 7종(췌장·폐·심장·대장·간·뇌·신장)을 반환하지만 화면에는 6종만 있다.
 * '폐'는 3D 모델(public/model)이 없어 표시 대상에서 빠진다 — 모델이 추가되면
 * 여기에 한 줄 넣고 ORGAN_MARKERS 에 좌표만 더하면 된다.
 */
export const ORGAN_KO_NAME: Record<string, string> = {
  brain: '뇌',
  heart: '심장',
  kidneys: '신장',
  liver: '간',
  intestine: '대장',
  pancreas: '췌장',
};

/** 서버가 주지만 화면에 대응 모델이 없는 장기 */
export const UNMAPPED_ORGAN_NAMES = ['폐'];

/**
 * 장기 위험 여부. 스펙의 riskGrade 는 'Grade 0'(정상) / 'Grade 1'(위험).
 * Grade 2 이상이 생기더라도 0 이 아니면 위험으로 본다.
 */
export const isOrganAtRisk = (organ?: OrganRisk | null): boolean => {
  if (!organ) return false;
  const m = /Grade\s*(\d+)/i.exec(organ.riskGrade ?? '');
  if (!m) return false;
  return Number(m[1]) > 0;
};

// 성별 판정(isMaleSex)은 심리 홈 화면과 공용이라 utils/bodyModel 로 옮겼다.

// ── 문진 제출 여부 ────────────────────────────────────────────

/**
 * 이 계정이 이미 건강 문진을 제출했는지.
 *
 * 판단 근거를 서버(`user.survey`)에 두는 이유: 문진은 "아이디 당 1회"이므로
 * localStorage 플래그로는 안 된다 — 기기를 바꾸거나 스토리지를 비우면 다시 묻게 되고,
 * 반대로 같은 기기의 다른 계정에서 건너뛰어질 수 있다.
 *
 * 채워진 survey 의 형태가 스펙에 없어 보수적으로 판정한다.
 * 빈 객체·빈 배열·빈 문자열은 '미제출'로 본다 — 잘못 '제출됨'으로 읽으면
 * 사용자가 문진을 영구히 작성할 수 없게 되므로 그쪽 위험이 더 크다.
 */
export const hasSubmittedSurvey = (data: DigitalTwinData | null | undefined): boolean => {
  const s = data?.user?.survey;
  if (s === null || s === undefined) return false;
  if (typeof s === 'string') return s.trim() !== '';
  if (typeof s === 'number') return true;
  if (typeof s === 'object') return Object.keys(s as object).length > 0;
  return Boolean(s);
};

// ── 질환 등급 → 화면 배지 ─────────────────────────────────────

export type DiseaseBadge = '나쁨' | '주의' | '양호';

/**
 * 서버 riskGrade 문구를 화면의 3단 배지로 환산한다.
 *
 * riskGrade 는 서버가 생성하는 자유 문구이고 전체 어휘 목록이 스펙에 없다.
 * 확인된 예: '건강 - 현재 당뇨 위험 낮음', '미세 위험 - 혈압 관리 필요',
 *            '과체중/미세 위험 - 체중관리 필요'
 *
 * 그래서 (1) ' - ' 앞의 등급부만 보고 (2) 못 알아본 문구는 '양호'가 아니라
 * '주의'로 떨어뜨린다. 모르는 등급이 건강으로 표시되는 쪽이 훨씬 위험하다.
 * matched=false 면 호출부가 서버 원문을 함께 노출하도록 한다.
 */
export const resolveDiseaseBadge = (
  riskGrade: string | undefined | null,
): { badge: DiseaseBadge; matched: boolean } => {
  const head = (riskGrade ?? '').split(' - ')[0].trim();
  if (head === '') return { badge: '주의', matched: false };

  // 순서 중요 — '건강 - 현재 당뇨 위험 낮음'처럼 '위험'이 뒤에 섞여 나온다
  if (/고위험|높은\s*위험|위험\s*높|중대|심각/.test(head)) return { badge: '나쁨', matched: true };
  if (/건강|정상|양호/.test(head)) return { badge: '양호', matched: true };
  if (/위험|과체중|비만|주의|경계|관리/.test(head)) return { badge: '주의', matched: true };

  return { badge: '주의', matched: false };
};

// ── 장기·질환 공통 조회 ───────────────────────────────────────

/**
 * 화면의 장기 키(brain·heart…)로 AP_009_002 의 장기 판단을 찾는다.
 * 서버는 한글 장기명을 키로 주므로 ORGAN_KO_NAME 을 거쳐야 한다.
 */
export const findOrganRisk = (
  data: DigitalTwinData | null | undefined,
  organKey: string,
): OrganRisk | undefined => data?.organ?.[ORGAN_KO_NAME[organKey]];

/**
 * 장기 riskGrade('Grade 0' | 'Grade 1' …)를 화면 문구로 바꾼다.
 * 질환의 resolveDiseaseBadge 와 달리 장기는 Grade 숫자만 오므로 별도로 둔다.
 */
export const organGradeLabel = (organ?: OrganRisk | null): string => {
  if (!organ) return '미측정';
  const m = /Grade\s*(\d+)/i.exec(organ.riskGrade ?? '');
  if (!m) return organ.riskGrade || '미측정';
  const g = Number(m[1]);
  if (g <= 0) return '양호';
  if (g === 1) return '주의';
  return '나쁨';
};

/**
 * 화면에 그대로 쓸 수 있는 '신뢰도 / 데이터 충족률 / 위험 점수' 3종.
 * 장기·질환 어느 쪽이든 같은 필드를 가지므로 하나로 처리한다.
 * 값이 없으면 null 을 주어 호출부가 '미측정'으로 표시하게 한다.
 */
export type RiskSummary = {
  confidence: string | null;
  coverage: number | null;
  score: number | null;
};

export const toRiskSummary = (
  r: OrganRisk | DiseaseRiskDetail | undefined | null,
): RiskSummary => ({
  confidence: r?.confidenceLevel ?? null,
  coverage: typeof r?.dataCoverageRate === 'number' ? Math.round(r.dataCoverageRate) : null,
  score: typeof r?.score === 'number' ? Math.round(r.score) : null,
});

// ── 검진 지표 → 화면 타일 ─────────────────────────────────────

export type VitalMetricView = {
  id: string;
  label: string;
  /** 표시 문자열. 값이 없으면 missing=true 이고 value 는 '미측정'. */
  value: string;
  missing: boolean;
};

const fmt = (n: number | null, digits: number): string | null =>
  n === null || n === undefined || Number.isNaN(n) ? null : n.toFixed(digits);

/**
 * 하단 타일(4칸)을 검진 지표에서 만든다.
 *
 * 값 색상(정상/비정상)은 넣지 않는다. 이 프로젝트의 정상범위 기준이 여러 곳에
 * 서로 모순된 상태(deviceInfo.getPressureStatus 101~120 vs METRIC_DEF 90~120)라,
 * 여기서 새 기준을 임의로 정하면 같은 문제를 하나 더 만든다.
 * 기준을 단일화한 뒤 이 함수에 등급을 추가하는 것이 맞다.
 *
 * 혈색소(Hb)는 스펙의 지표 18종에 없어 기존 '혈색소' 타일을 공복혈당으로 교체했다.
 */
export const buildVitalMetrics = (h: HealthIndicators | undefined | null): VitalMetricView[] => {
  const hi = h ?? ({} as HealthIndicators);
  const bp =
    hi.SYSTOLIC_BP !== null && hi.SYSTOLIC_BP !== undefined &&
    hi.DIASTOLIC_BP !== null && hi.DIASTOLIC_BP !== undefined
      ? `${Math.round(hi.SYSTOLIC_BP)}/${Math.round(hi.DIASTOLIC_BP)} mmHg`
      : null;

  const rows: { id: string; label: string; text: string | null }[] = [
    { id: 'height', label: '신장', text: withUnit(fmt(hi.HEIGHT, 1), 'cm') },
    { id: 'weight', label: '체중', text: withUnit(fmt(hi.WEIGHT, 1), 'kg') },
    // 허리둘레(WAIST_CIRCUMFERENCE)·체질량지수(BMI) 타일은 표시하지 않는다.
    // AP_009_002 응답에는 그대로 내려오며 질환 판단 근거로는 계속 쓰인다.
    { id: 'blood-pressure', label: '혈압(최고/최저)', text: bp },
    { id: 'glucose', label: '혈당', text: withUnit(fmt(hi.FASTING_GLUCOSE, 1), 'mg/dL') },
  ];

  return rows.map((r) => ({
    id: r.id,
    label: r.label,
    value: r.text ?? '미측정',
    missing: r.text === null,
  }));
};

const withUnit = (v: string | null, unit: string) => (v === null ? null : `${v} ${unit}`);

// ── 호출 ──────────────────────────────────────────────────────

/**
 * POST app/api/v1/AP_009_002 — 질환·장기 DT 호출
 * 헤더 X-CUSTOMER-ID = familyId (= localStorage.ownerFamID)
 */
/** 이 응답이 어느 가족 구성원 것인지 — 캐시 키로도 쓰인다 */
const currentFamilyId = () => window.localStorage.getItem('ownerFamID') ?? '';

export const fetchDigitalTwin = async (): Promise<DigitalTwinData> => {
  const familyId = currentFamilyId();
  if (familyId === '') throw userFacingError('로그인 정보가 없습니다.');

  const res = await api.post('app/api/v1/AP_009_002', {}, {
    headers: { 'X-CUSTOMER-ID': familyId },
  });

  if (res.data?.success !== true) {
    // 서버 message 를 그대로 쓰지 않는다 — 실패 시 못 찾은 familyId 를 message 에 담는 것을 실측했다.
    // 진단은 콘솔로 남기고 사용자에게는 안전한 문구를 보여준다.
    console.warn('[DigitalTwin] AP_009_002 실패 응답:', res.data);
    throw userFacingError('건강 데이터를 불러오지 못했습니다.');
  }
  const data = res.data.data;
  if (!data || typeof data !== 'object') {
    throw userFacingError('건강 데이터 형식이 올바르지 않습니다.');
  }
  return data as DigitalTwinData;
};

// ── 캐시 + 훅 ─────────────────────────────────────────────────

/**
 * 화면 1·2·5 가 같은 판단 결과를 쓴다 — 요청을 한 번만 보내고 값을 공유한다.
 * 캐시 키가 familyId 이므로 로그아웃·계정 전환 시 이전 계정 데이터가 재사용되지 않는다.
 */
const twinResource = createCachedResource(fetchDigitalTwin, currentFamilyId);

export const getDigitalTwin = (force = false) => twinResource.get(force);

/** 문진 저장처럼 판단 근거가 바뀌는 동작 뒤에 호출한다. */
export const invalidateDigitalTwin = () => twinResource.invalidate();

export type UseDigitalTwin = UseResource<DigitalTwinData>;

export const useDigitalTwin = (): UseDigitalTwin =>
  useCachedResource(twinResource, '건강 데이터를 불러오지 못했습니다.');
